import React from "react";
import styles from "./tickets-list.module.scss";
import { SupportAgentTicket } from "@src/types/support";
import TicketCard from "@src/components/molecules/ticket-card/ticket-card";
import { TicketFilterTab } from "@src/components/atoms/pill-filtering/use-pill-filtering";
import { PillFiltering } from "@src/components/atoms/pill-filtering/pill-filtering";
import { useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";
import { useI18N } from "@src/language/language-context";

export enum TicketFilter {
  ALL = "all",
  UNASSIGNED = "unassigned",
  MY_TICKETS = "my_tickets",
  SOLVED = "solved",
}

const cssClassName = "tickets-list";

interface TicketsListProps {
  tickets: SupportAgentTicket[];
  selectedTicketId?: string | null;
  onSelectTicket: (ticket: SupportAgentTicket) => void;
}

const isAIAssignedTicket = (ticket: SupportAgentTicket) =>
  ticket.handled_by_ai_chatbot;

// "solved" is a terminal state: a ticket belongs either to the solved tab or to
// the unassigned/my-tickets tabs, never to both
const isSolvedTicket = (ticket: SupportAgentTicket) =>
  ticket.status === "closed" || ticket.status === "solved";

export const TicketsList = ({
  tickets,
  selectedTicketId,
  onSelectTicket,
}: TicketsListProps) => {
  const i18n = useI18N();
  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
  );

  const language = useSelector((state: RootState) => state.yourSlice.language);

  const texts = {
    all: i18n("modules.ticketList.tabsAll"),
    unassigned: i18n("modules.ticketList.tabsUnassigned"),
    myTickets: i18n("modules.ticketList.tabsMyTickets"),
    solved: i18n("modules.ticketList.tabsSolved"),
  };

  const TABS: TicketFilterTab<TicketFilter>[] = React.useMemo(
    () => [
      {
        label: texts.all,
        filterKey: TicketFilter.ALL,
      },
      {
        label: texts.unassigned,
        filterKey: TicketFilter.UNASSIGNED,
        countFn: (tickets) =>
          tickets.filter(
            (t) =>
              t.assigned_to === null &&
              !isAIAssignedTicket(t) &&
              !isSolvedTicket(t),
          ).length,
      },
      {
        label: texts.myTickets,
        filterKey: TicketFilter.MY_TICKETS,
        countFn: (tickets) =>
          tickets.filter((t) => t.assigned_to?.id === authContext.userId)
            .length,
      },
      {
        label: texts.solved,
        filterKey: TicketFilter.SOLVED,
        countFn: (tickets) => tickets.filter(isSolvedTicket).length,
      },
    ],
    [authContext.userId, language],
  );

  const [activeFilter, setActiveFilter] = React.useState<TicketFilter>(
    TicketFilter.ALL,
  );

  const filteredTickets = React.useMemo(() => {
    switch (activeFilter) {
      case TicketFilter.UNASSIGNED:
        return tickets.filter(
          (t) =>
            t.assigned_to === null &&
            !isAIAssignedTicket(t) &&
            !isSolvedTicket(t),
        );
      case TicketFilter.MY_TICKETS:
        return tickets.filter((t) => t.assigned_to?.id === authContext.userId);
      case TicketFilter.SOLVED:
        // we want to show solved tickets even if they are assigned to the AI
        return tickets.filter(isSolvedTicket);
      case TicketFilter.ALL:
      default:
        // live agent inbox: solved tickets live in their own tab, AI ones never
        // reach this list
        return tickets.filter(
          (t) => !isAIAssignedTicket(t) && !isSolvedTicket(t),
        );
    }
  }, [activeFilter, tickets, authContext.userId]);

  return (
    <div className={styles[cssClassName]}>
      <PillFiltering<TicketFilter>
        tabs={TABS}
        activeFilter={activeFilter}
        onFilter={setActiveFilter}
        tickets={tickets}
      />
      <div className={styles[`${cssClassName}__cards-wrapper`]}>
        {filteredTickets.map((ticket) => (
          <TicketCard
            key={ticket.id}
            ticket={ticket}
            onSelectTicket={() => onSelectTicket(ticket)}
            selectedTicketId={selectedTicketId}
          />
        ))}
      </div>
    </div>
  );
};
